import * as React from "react";
import { Notify } from "@/components/ui/notify";
import { useOee } from "@/features/oee/hooks";
import { useActiveRun } from "@/features/production/hooks";
import { cn } from "@/lib/utils";
import { VitalCell, V_NUM, V_NUM_UNIT, V_NUM_SLASH, V_SUB } from "./_vitals-cells";
import { VitalsOeeHero } from "./_vitals-oee-hero";
import { OeeMetricExplanationDialog } from "./OeeMetricExplanationDialog";
import { PlanAdherenceKpi } from "./PlanAdherenceKpi";
import type { LinePeriodFilter } from "@/types/period-filter.types";
import type { OeeBreakdownDetail } from "@/types/oee.types";
import { formatQuantity } from "@/lib/quantity-format";

export interface VitalsBarProps {
  lineId: string;
  period: LinePeriodFilter;
  className?: string;
}

const PRESET_BACKEND_PERIOD: Record<Exclude<LinePeriodFilter, "active">, "24h" | "7d" | "30d"> = {
  "24h": "24h",
  "7d": "7d",
  "30d": "30d",
};

const PERIOD_LABEL: Record<LinePeriodFilter, string> = {
  active: "Ordem ativa",
  "24h": "Últimos 24h",
  "7d": "Últimos 7 dias",
  "30d": "Últimos 30 dias",
};

function floorToMinuteIso(d: Date): string {
  const x = new Date(d);
  x.setSeconds(0, 0);
  return x.toISOString();
}

function fmtInt(v: number | null | undefined): string {
  return v != null && Number.isFinite(v) ? v.toLocaleString("pt-BR") : "—";
}

function fmtPct(num: number, den: number): number {
  return den > 0 ? Math.round((num / den) * 100) : 0;
}

export function VitalsBar({ lineId, period, className }: VitalsBarProps): React.ReactElement {
  const { data: run } = useActiveRun(lineId);
  const customWindow =
    period === "active" && run?.startedAt
      ? { from: run.startedAt, to: floorToMinuteIso(new Date()) }
      : undefined;
  const oeeEnabled = period !== "active" || !!customWindow;
  const oeeQuery = useOee(lineId, period === "active" ? "24h" : PRESET_BACKEND_PERIOD[period], {
    window: customWindow,
    enabled: oeeEnabled,
    compareWithPrevious: true,
    breakdown: true,
  });
  const { data: oee } = oeeQuery;
  const [openMetric, setOpenMetric] = React.useState<keyof OeeBreakdownDetail | null>(null);

  // Maior severidade: sem isto, erro de fetch vira "—"/0 silencioso na barra de KPIs
  // principal da tela da linha. Substitui a barra inteira (todas as células dependem de `oee`).
  if (oeeQuery.isError) {
    return (
      <div
        role="region"
        aria-label="Indicadores chave da linha"
        className={cn(
          "mt-5 overflow-hidden rounded-2xl border border-border-subtle bg-bg-surface p-4",
          className
        )}
      >
        <Notify.QueryError
          variant="inline"
          error={oeeQuery.error}
          onRetry={() => void oeeQuery.refetch()}
          title="Não foi possível carregar os indicadores"
        />
      </div>
    );
  }

  const r = oee?.raw;
  const breakdown = oee?.planAdherenceBreakdown;
  const producedQty = breakdown?.producedQty ?? run?.producedQty ?? 0;
  const plannedQty = breakdown?.plannedQty ?? run?.plannedQty ?? 0;
  const produzidoPct = fmtPct(producedQty, plannedQty);
  const totalLoss = r?.totalLoss ?? 0;
  const lossPctMeta = r && r.plannedQty > 0 ? Math.round((r.totalLoss / r.plannedQty) * 100) : 0;
  const hasLosses = totalLoss > 0;

  return (
    <div
      role="region"
      aria-label="Indicadores chave da linha"
      className={cn(
        "mt-5 grid grid-cols-1 overflow-hidden rounded-2xl border border-border-subtle bg-bg-surface",
        "lg:grid-cols-[1.55fr_1.1fr_0.9fr_0.9fr_1fr]",
        className
      )}
    >
      {}
      <VitalsOeeHero oee={oee} period={period} onOpenMetric={setOpenMetric} />

      {}
      <VitalCell
        label={
          <div className="flex items-center justify-between gap-2.5">
            <span>Produzido · {period === "active" ? "Ativa" : period}</span>
            <PlanAdherenceKpi
              planAdherence={oee?.planAdherence}
              planAdherenceBreakdown={oee?.planAdherenceBreakdown}
              variant="inline"
              periodLabel={PERIOD_LABEL[period]}
            />
          </div>
        }
      >
        <div className="flex items-baseline gap-1">
          <span className={V_NUM}>{formatQuantity(producedQty)}</span>
          {plannedQty > 0 ? (
            <span className={cn(V_NUM, V_NUM_SLASH)}> / {formatQuantity(plannedQty)} un</span>
          ) : (
            <span className={V_NUM_UNIT}> un</span>
          )}
        </div>
        {plannedQty > 0 ? (
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg-muted-rd">
            <div
              className="h-full w-[var(--prod-w)] rounded-full bg-brand-600"
              style={{ "--prod-w": `${Math.min(100, produzidoPct)}%` } as React.CSSProperties}
            />
          </div>
        ) : null}
        <span className={V_SUB}>
          {plannedQty > 0 ? `${produzidoPct}% da meta` : "Sem meta capturada"}
        </span>
      </VitalCell>

      {}
      <VitalCell label="Downtime">
        <div className="flex items-baseline gap-1">
          <span className={V_NUM}>{fmtInt(r?.downtimeMinutes)}</span>
          <span className={V_NUM_UNIT}>min</span>
        </div>
        <span className={V_SUB}>
          {r && r.openStops === 0 ? "Sem paradas abertas" : `${r?.openStops ?? 0} aberta(s)`}
        </span>
      </VitalCell>

      {}
      <VitalCell label="Execuções">
        <span className={V_NUM}>{fmtInt(r?.runCount)}</span>
        <span className={V_SUB}>{run ? "Ordem em andamento" : "Sem ordem ativa"}</span>
      </VitalCell>

      {}
      <VitalCell
        label="Perdas"
        className={cn(hasLosses && "bg-gradient-to-b from-[#fffbfa] to-rd-danger-50")}
      >
        <div className="flex items-baseline gap-1">
          <span className={V_NUM}>{fmtInt(totalLoss)}</span>
          <span className={V_NUM_UNIT}>un</span>
        </div>
        <span className={cn(V_SUB, hasLosses && "text-rd-danger-700")}>
          {hasLosses ? `${lossPctMeta}% da meta` : "Sem perdas no período"}
        </span>
      </VitalCell>

      {}
      <OeeMetricExplanationDialog
        metric={openMetric}
        breakdown={oee?.breakdown}
        periodLabel={PERIOD_LABEL[period]}
        open={openMetric !== null}
        onClose={() => setOpenMetric(null)}
      />
    </div>
  );
}
